import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { getLocale } from '@infl-ts/util-date';
import { DE$ } from '@infl-ts/util-datum';
import { S$ } from '@infl-ts/util-fp-ts';
import { useToggle } from '@infl-ts/util-hooks';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import * as DE from '@nll/datum/lib/DatumEither';
import { constVoid, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { createContext, useCallback, useContext } from 'react';
import { I18nextProvider, useSSR } from 'react-i18next';
import { i18nInstance } from './localization';
import { useUpdateProfileLanguage } from './mutation';
import { useConsentStatus, useProfileLanguage } from './queries';
import { saveFailure } from './alerts';
import { jsx as _jsx } from "react/jsx-runtime";
var DEFAULT_LANG = 'en';

var getDefaultLanguage = function getDefaultLanguage(cookieLang, hubDefaultLang) {
  return cookieLang || hubDefaultLang || DEFAULT_LANG;
};

var useFetchProfileLang = function useFetchProfileLang(_ref) {
  var skip = _ref.skip;
  return pipe(useProfileLanguage({
    skip: skip
  }), DE.map(function (_ref2) {
    var code = _ref2.profile.language.code;
    return code;
  }));
};

var LanguageContext = /*#__PURE__*/createContext({
  currentLang: DEFAULT_LANG,
  setLang: constVoid,
  profileMutationStatus: DE.initial
});

var handlePossiblyUndefinedI18NLanguage = function handlePossiblyUndefinedI18NLanguage(i18n) {
  return i18n.language || DEFAULT_LANG;
};

var reloadPage = function reloadPage() {
  var _window, _window$location;

  return (_window = window) === null || _window === void 0 ? void 0 : (_window$location = _window.location) === null || _window$location === void 0 ? void 0 : _window$location.reload();
};

var LanguageProvider = function LanguageProvider(_ref3) {
  var i18n = _ref3.i18n,
      isPublicPage = _ref3.isPublicPage,
      defaultLocales = _ref3.defaultLocales,
      children = _ref3.children;

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useToggle = useToggle(),
      _useToggle2 = _slicedToArray(_useToggle, 2),
      _ = _useToggle2[0],
      forceRerender = _useToggle2[1].toggle;

  var _useUpdateProfileLang = useUpdateProfileLanguage(),
      _useUpdateProfileLang2 = _slicedToArray(_useUpdateProfileLang, 2),
      editProfile = _useUpdateProfileLang2[0],
      profileMutationStatus = _useUpdateProfileLang2[1];

  var changeLanguage = useCallback(function (language, options) {
    document.cookie = "lang=".concat(language, ";path=/");

    if (isPublicPage) {
      void i18n.changeLanguage(language, options !== null && options !== void 0 && options.reload ? reloadPage : forceRerender);
    } else {
      editProfile({
        variables: {
          input: {
            language: language
          }
        },
        onCompleted: function onCompleted() {
          if (options !== null && options !== void 0 && options.reload) {
            reloadPage();
          }
        },
        onError: function onError() {
          showAlert(saveFailure(i18n.t));
        }
      });
    }
  }, [i18n, forceRerender]);
  var currentLang = handlePossiblyUndefinedI18NLanguage(i18n);
  useSSR(defaultLocales, currentLang);
  return /*#__PURE__*/_jsx(LanguageContext.Provider, {
    value: {
      currentLang: currentLang,
      setLang: changeLanguage,
      profileMutationStatus: profileMutationStatus
    },
    "data-element": "unknown",
    "data-component": "LanguageProvider",
    "data-source-file": "i18n-provider.tsx",
    children: /*#__PURE__*/_jsx(LocalizationProvider, {
      dateAdapter: AdapterDateFns,
      adapterLocale: getLocale(currentLang),
      "data-element": "LocalizationProvider",
      "data-component": "LanguageProvider",
      "data-source-file": "i18n-provider.tsx",
      children: /*#__PURE__*/_jsx(I18nextProvider, {
        i18n: i18n,
        "data-element": "I18nextProvider",
        "data-component": "LanguageProvider",
        "data-source-file": "i18n-provider.tsx",
        children: children
      })
    })
  });
};

export var I18nProvider = function I18nProvider(_ref4) {
  var options = _ref4.options,
      cookieLang = _ref4.cookieLang,
      defaultLocales = _ref4.defaultLocales,
      isPublicPage = _ref4.isPublicPage,
      pageRequiresConsent = _ref4.pageRequiresConsent,
      children = _ref4.children;
  var shouldUseCookieLang = pipe(useConsentStatus({
    skip: !pageRequiresConsent || isPublicPage
  }), DE.map(S$.prop('consentStatus')), DE$.getSuccessOrElse(false), function (consentStatus) {
    return pageRequiresConsent && !consentStatus;
  }, function (hasNotConsented) {
    return isPublicPage || hasNotConsented;
  });
  var profileLang = useFetchProfileLang({
    skip: shouldUseCookieLang
  });
  return pipe(options, DE.map(O.map(function (_ref5) {
    var hubDefaultLang = _ref5.hubDefaultLang;
    return shouldUseCookieLang || !DE.isSuccess(profileLang) ? DE.fromNullable(getDefaultLanguage(cookieLang, hubDefaultLang)) : pipe(profileLang.value.right, function (code) {
      return !code || code === '' ? getDefaultLanguage(cookieLang, hubDefaultLang) : code;
    }, DE.fromNullable);
  })), DE.chain(O.getOrElse(function () {
    return DE.fromNullable(DEFAULT_LANG);
  })), DE$.getSuccessOrElse(DEFAULT_LANG), i18nInstance, function (i18n) {
    return /*#__PURE__*/_jsx(LanguageProvider, {
      isPublicPage: isPublicPage,
      defaultLocales: defaultLocales,
      i18n: i18n,
      children: children
    });
  });
};
export var useLanguage = function useLanguage() {
  var _useContext2 = useContext(LanguageContext),
      currentLang = _useContext2.currentLang,
      setLang = _useContext2.setLang,
      profileMutationStatus = _useContext2.profileMutationStatus;

  return {
    currentLang: currentLang,
    setLang: setLang,
    profileMutationStatus: profileMutationStatus
  };
};